import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, photoUrl } from "@/lib/api";
import { Heart, X, Clock, Flame } from "lucide-react";
import { toast } from "sonner";

export default function Matching() {
  const nav = useNavigate();
  const [session, setSession] = useState(null);
  const [candidate, setCandidate] = useState(null);
  const [remaining, setRemaining] = useState("");
  const [loading, setLoading] = useState(true);
  const [matchModal, setMatchModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/matching/candidate");
      if (data.active_match_id) {
        nav(`/matches/${data.active_match_id}`);
        return;
      }
      setCandidate(data.candidate);
      setSession(data.session || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No active session");
      nav("/dashboard");
    } finally { setLoading(false); }
  }, [nav]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!session) return;
    const t = setInterval(() => {
      const diff = new Date(session.expires_at).getTime() - Date.now();
      if (diff <= 0) { setRemaining("Expired"); nav("/dashboard"); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setRemaining(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`);
    }, 1000);
    return () => clearInterval(t);
  }, [session, nav]);

  const act = async (action) => {
    if (!candidate) return;
    try {
      if (action === "CONNECT") {
        const { data } = await api.post("/matching/connect", { candidate_user_id: candidate.id });
        if (data.matched) {
          setSession(null); // stop timer immediately
          setMatchModal(data.match_id);
          return;
        }
      } else {
        await api.post("/matching/next", { candidate_user_id: candidate.id });
      }
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  return (
    <div className="min-h-screen bg-dc-gradient flex flex-col">
      <nav className="flex items-center justify-between p-4 max-w-xl w-full mx-auto">
        <Link to="/dashboard" className="font-display text-lg tracking-widest text-white">DILCONNECT</Link>
        <div data-testid="match-timer" className="font-mono text-sm px-3 py-1.5 rounded-xl border border-amber-500/40 text-amber-300 bg-black/60 flex items-center gap-2">
          <Clock className="w-3.5 h-3.5" /> {remaining || "--:--:--"}
        </div>
      </nav>

      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          {loading ? (
            <div className="glass-strong rounded-3xl h-[520px] flex items-center justify-center text-purple-200">Loading…</div>
          ) : candidate ? (
            <div data-testid="candidate-card" className="glass-strong rounded-3xl overflow-hidden">
              <div className="relative h-[460px] bg-black/60">
                {candidate.photo_ids?.[0] ? (
                  <img
                    src={photoUrl(candidate.photo_ids[0])}
                    alt=""
                    className="w-full h-full object-cover select-none"
                    draggable={false}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-purple-200/50">No photo</div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                <div className="absolute left-4 right-4 bottom-4 text-white">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-pink-200">
                    <Flame className="w-3.5 h-3.5" /> Contact revealed only after mutual connect
                  </div>
                  <div className="mt-2 font-display text-3xl font-black leading-tight">
                    {candidate.display_name}{candidate.verified ? " ✓" : ""} <span className="text-xl font-bold text-purple-100/90">· {candidate.age}</span>
                  </div>
                  <div className="text-sm text-purple-100/90">{candidate.city}</div>
                </div>
              </div>
              {(candidate.bio || (candidate.languages?.length ?? 0) || (candidate.interests?.length ?? 0)) && (
                <div className="px-5 pt-4 pb-2 text-sm text-purple-100/90 space-y-2">
                  {candidate.bio && <div>{candidate.bio}</div>}
                  {candidate.languages?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {candidate.languages.map((l) => (
                        <span key={l} className="text-[11px] px-2 py-0.5 rounded-full bg-purple-900/60 border border-purple-500/30">{l}</span>
                      ))}
                    </div>
                  )}
                  {candidate.interests?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {candidate.interests.map((i) => (
                        <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-fuchsia-900/50 border border-fuchsia-500/30">{i}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="p-5 flex items-center justify-between gap-3">
                <button data-testid="next-btn" onClick={() => act("NEXT")} className="flex-1 py-3 rounded-2xl bg-purple-900/60 border border-purple-500/30 text-white font-bold hover:bg-purple-900/80 flex items-center justify-center gap-2">
                  <X className="w-5 h-5" /> Next
                </button>
                <button data-testid="connect-btn" onClick={() => act("CONNECT")} className="flex-1 py-3 rounded-2xl bg-gradient-to-r from-pink-500 to-fuchsia-600 text-white font-bold shadow-lg flex items-center justify-center gap-2">
                  <Heart className="w-5 h-5" /> Connect
                </button>
              </div>
            </div>
          ) : (
            <div data-testid="no-candidate" className="glass-strong rounded-3xl p-8 text-center">
              <div className="text-5xl mb-4">💜</div>
              <h2 className="font-display text-xl font-extrabold">No suitable connection right now</h2>
              <p className="text-sm text-purple-200/70 mt-2">Check back soon — new members are being approved continuously.</p>
              <Link to="/dashboard" className="btn-cta mt-6 inline-block px-5 py-2.5 rounded-xl">Back to dashboard</Link>
            </div>
          )}
        </div>
      </main>

      {matchModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50" data-testid="match-modal">
          <div className="glass-strong rounded-3xl p-8 text-center max-w-sm">
            <Heart className="w-16 h-16 text-pink-400 mx-auto animate-pulse" fill="currentColor" />
            <h2 className="font-display text-3xl font-black mt-4">It's a Match!</h2>
            <p className="text-purple-200/80 mt-2">You both connected.</p>
            <div className="mt-6 flex gap-3">
              <button data-testid="match-continue" onClick={() => { setMatchModal(null); load(); }} className="flex-1 py-3 rounded-xl bg-purple-900/60 border border-purple-500/30">Continue</button>
              <button data-testid="match-view" onClick={() => nav(`/matches/${matchModal}`)} className="flex-1 py-3 rounded-xl btn-cta">View Match</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
